/**
 * @author michalstolarski
 * @since 2022-10-13
 */

import * as React from "react";
import styled, { css } from "styled-components";

import { TestWrapper } from "@ui/Atoms/TestWrapper";

type Variant =
  | "default"
  | "info"
  | "warning"
  | "warning-dark"
  | "danger"
  | "success"
  | "success-light";

type Size = "default" | "overline";

interface Props {
  testId?: string;
  className?: string;
  children?: React.ReactNode;
  variant: Variant;
  size?: Size;
}

const InfoLabel = ({
  size = "default",
  ...props
}: Props): React.ReactElement => (
  <TestWrapper testId={props.testId}>
    <Label className={props.className} variant={props.variant} size={size}>
      {props.children}
    </Label>
  </TestWrapper>
);

interface InfoLabelWithIconProps extends Props {
  icon?: React.ReactNode;
}

const InfoLabelWithIcon = ({
  icon,
  size = "default",
  ...props
}: InfoLabelWithIconProps): React.ReactElement => (
  <LabelWithGap {...props} size={size}>
    {!!icon && icon}
    {props.children}
  </LabelWithGap>
);

export { Variant as InfoLabelVariant, InfoLabel, InfoLabelWithIcon };

const createBgColor = (variant: Variant, color: string): string =>
  `--info-label-bg-color--${variant}: ${color}`;
const createTextColor = (variant: Variant, color: string): string =>
  `--info-label-color--${variant}: ${color}`;

const Label = styled.div<{ variant?: Variant; size: Size }>`
  /* variant -- default */
  ${props => createTextColor("default", props.theme.palette.grey800)};
  ${props => createBgColor("default", props.theme.palette.grey20)};

  /* variant -- info */
  ${props => createTextColor("info", props.theme.palette.blue700)};
  ${props => createBgColor("info", props.theme.palette.blue20)};

  /* variant -- warning */
  ${props => createTextColor("warning", props.theme.palette.grey800)};
  ${props => createBgColor("warning", props.theme.palette.orange50)};

  /* variant -- warning-dark */
  ${props => createTextColor("warning-dark", props.theme.palette.white)};
  ${props => createBgColor("warning-dark", props.theme.palette.grey800)};

  /* variant -- danger */
  ${props => createTextColor("danger", props.theme.palette.white)};
  ${props => createBgColor("danger", props.theme.palette.red700)};

  /* variant -- success */
  ${props => createTextColor("success", props.theme.palette.white)};
  ${props => createBgColor("success", props.theme.palette.green500)};

  /* variant -- success-light */
  ${props => createTextColor("success-light", props.theme.palette.green900)};
  ${props => createBgColor("success-light", props.theme.palette.green50)};

  display: flex;
  align-items: center;
  width: fit-content;
  font-weight: ${props => props.theme.fonts.weights.normal};
  border-radius: ${props => props.theme.buttons.borderRadius};

  color: var(
    --info-label-color--${props => props.variant},
    ${props => props.theme.palette.blue700}
  );
  background-color: var(
    --info-label-bg-color--${props => props.variant},
    ${props => props.theme.palette.blue20}
  );

  /* size */
  ${props => {
    return {
      ["default"]: css`
        padding: ${props => props.theme.margins.half}
          ${props => props.theme.margins.base};
        font-size: ${props => props.theme.fonts.sizes.caption};
      `,
      ["overline"]: css`
        padding: 0 ${props => props.theme.margins.half};
        font-size: ${props => props.theme.fonts.sizes.overline};
        line-height: ${props => props.theme.fonts.lineHeights.caption};
      `,
    }[props.size];
  }}
`;

const LabelWithGap = styled(Label)`
  gap: ${props => props.theme.margins.base};
`;
